/** 2025 is the first year with a configured THG rate, so nothing earlier is offered. */
export const FIRST_THG_YEAR = 2025;

export const THG_TABLE_ROWS = 25;

export const COPY = {
  provisionalBanner: "Provisional — figures can still change. Do not pay out from this.",
  provisionalCsvNote: "PROVISIONAL — figures can still change. Do not pay out from this.",
  excludedSessions: (n: number) =>
    `${n} ${n === 1 ? "session was" : "sessions were"} excluded (no station ID or negative energy).`,
  stationsWithoutOwner: (n: number) =>
    `${n} ${n === 1 ? "station has" : "stations have"} no owner assigned and cannot be paid.`,
  // Deliberately does not name the clearing setting: Back Office copy must not expose internal names.
  rateNotConfigured: (year: number) =>
    `No THG rate is configured for ${year}, so no payouts can be calculated. Ask the development team to add the ${year} rate.`,
  accessDenied: "You do not have access to THG payouts. An administrator account is required.",
  badYear: (year: number) => `${year} cannot be shown.`,
  loadFailed: (year: number) => `Could not load the THG payouts for ${year}.`,
  loading: (year: number) => `Calculating THG figures for ${year}…`,
  emptyYear: (year: number) => `No station had charging sessions in ${year}.`,
  searchPlaceholder: "Search by owner, e-mail or station",
  noSearchMatch: (query: string) => `No station matches "${query.trim()}".`,
  searchMatches: (shown: number, total: number) => `${shown} of ${total} stations`,
  // With a search active the footer still shows the whole year, so it says so.
  totalAllStations: "Total (all stations)",
  recomputeConfirm: (year: number) =>
    `Recompute ${year} from the current session data at the currently configured rate? This replaces the stored figures.`,
  recomputed: (year: number) => `THG figures for ${year} recomputed.`,
  xlsxUnpayableLegend: "Orange rows: no owner assigned or owner record missing. These stations cannot be paid.",
  noOwner: "No owner assigned",
  ownerRecordMissing: "Owner record missing",
};

/**
 * CLR-283 payout credit notes. "Accounting system" rather than the vendor's name: Back Office copy names no
 * internal systems.
 */
export const PAYOUT_COPY = {
  generate: "Generate credit note",
  pdf: "PDF",
  sync: "Sync with accounting",
  generateHeader: (stationId: string) => `Generate credit note for ${stationId}`,
  generateConfirm: (stationId: string, year: number, recipient: string, net: string) =>
    `Generate a draft THG credit note for ${stationId} (${year}) to ${recipient}? Net ${net}; VAT is added per the owner's VAT treatment. Nothing is sent to the owner: the draft is finalised and sent in EasyBill.`,
  unknownRecipient: "the station's owner",
  generated: (stationId: string) => `Draft credit note created for ${stationId}.`,
  busy: "The accounting system is busy. Please try again in a moment.",
  busySummary: "Try again shortly",
  forbidden: "You do not have permission to manage THG credit notes.",
  notFound: (stationId: string) => `No credit note is available for ${stationId} any more.`,
  notEligible: (stationId: string, reason: string) => `${stationId} cannot get a credit note: ${reason}.`,
  reloaded: "The table has been reloaded.",
  rejected: (detail: string) =>
    `The accounting system did not accept the request. The table has been reloaded; check the row before trying again. Details: ${detail}`,
  reloadFailed: "Could not reload the payout status. Reload the page to see the latest status.",
  noNumberYet: "No number until finalised",
  /** One line near Sync: finalising is done outside the Back Office. */
  finalizeInAccounting:
    "Drafts are finalised and sent to the owner in EasyBill; Sync with accounting then picks up the result.",
  amountMismatch: "Amount differs from the current payout",
  amountMismatchDetail:
    "The payout changed after this credit note was drafted (recompute). Cancel it in the accounting system and generate a new one.",
  recipientMismatch: (recipient: string) => `Issued to ${recipient}; the station's owner has changed`,
  earlierCreditNotes: (n: number) => `${n} earlier credit ${n === 1 ? "note" : "notes"} cancelled`,
  syncNothing: (year: number) => `No draft or final credit notes to check for ${year}.`,
  syncChecked: (checked: number) =>
    `Checked ${checked} credit ${checked === 1 ? "note" : "notes"} with the accounting system`,
  syncMissing: (n: number) =>
    `${n} not found in the accounting system; their status was left unchanged.`,
  statusCheckedAt: (when: string) => `Status last checked with the accounting system ${when} (Berlin time)`,
  totalsNetGross: (net: string, gross: string | null) => (gross === null ? `Net ${net}` : `Net ${net} · Gross ${gross}`),
};
